/** Quadros novos do Início: consultas prontas (financeiro, jurídico, equipe), post-its e chat. */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { navigate } from "@/lib/router";
import { Card, Empty, Loading } from "./kit";
import { Icon } from "./icons";
import { toast } from "./feedback";

export interface QueryResult {
  key: string; title: string; summary?: string; big?: string; columns: string[]; rows: Array<Array<string | number | null>>;
  links: Array<string | null>; empty: string; financial: boolean; more?: string; choices?: Array<{ label: string; q: string }>;
  series?: Array<{ day: string; inCents: number; outCents: number }>; denied?: boolean;
}

/** Quadro do Início → consulta do motor. */
export const QUERY_WIDGETS: Record<string, { key: string; label: string; group: string; opts?: { name: "periodo" | "dias"; values: Array<[string, string]> } }> = {
  fin_saldo: { key: "saldo_contas", label: "Saldo das contas", group: "Financeiro" },
  fin_receber: { key: "a_receber", label: "A receber", group: "Financeiro", opts: { name: "periodo", values: [["hoje", "Hoje"], ["semana", "Semana"], ["mes", "Mês"]] } },
  fin_pagar: { key: "a_pagar", label: "A pagar", group: "Financeiro", opts: { name: "periodo", values: [["7d", "7 dias"], ["mes", "Mês"]] } },
  fin_inadimplencia: { key: "inadimplentes", label: "Inadimplência", group: "Financeiro" },
  fin_fluxo: { key: "fluxo_caixa", label: "Fluxo de caixa (30 dias)", group: "Financeiro" },
  fin_meta: { key: "recebido_meta", label: "Recebido no mês × meta", group: "Financeiro" },
  fin_repasses: { key: "repasses_pendentes", label: "Repasses pendentes", group: "Financeiro" },
  fin_recibos: { key: "recibos_emitir", label: "Recibos a emitir", group: "Financeiro" },
  intimacoes: { key: "intimacoes_pendentes", label: "Intimações não analisadas", group: "Jurídico" },
  parados: { key: "processos_parados", label: "Processos parados", group: "Jurídico", opts: { name: "dias", values: [["30", "30 dias"], ["60", "60 dias"], ["90", "90 dias"], ["180", "180 dias"]] } },
  revisoes: { key: "para_revisar", label: "Minhas revisões pendentes", group: "Jurídico" },
  atrasadas_equipe: { key: "atrasadas_equipe", label: "Atrasadas da equipe", group: "Equipe" },
  casos_mes: { key: "casos_novos", label: "Casos novos no mês", group: "Escritório" },
  captacao: { key: "captacao_mes", label: "Captação do mês", group: "Escritório" },
};

export function QueryWidget({ id, o }: { id: string; o?: Record<string, string | number | boolean> }): JSX.Element | null {
  const w = QUERY_WIDGETS[id]!;
  const params = w.opts ? { [w.opts.name]: o?.[w.opts.name] as string | undefined } : {};
  const { data, isLoading } = useQuery({
    queryKey: ["quadro", w.key, params], refetchInterval: 300_000,
    queryFn: () => api.get<QueryResult>(`/painel/quadro/${w.key}${qs({ ...params, limite: 30 })}`),
  });
  if (data?.denied) return null;
  const optLabel = w.opts ? w.opts.values.find(([v]) => v === String(o?.[w.opts!.name] ?? ""))?.[1] : null;
  return (
    <Card title={data?.title ?? `${w.label}${optLabel ? ` — ${optLabel}` : ""}`} actions={data?.more ? <button className="link-btn" onClick={() => navigate(data.more!)}>Ver tudo →</button> : undefined}>
      {isLoading || !data ? <Loading /> : (
        <div className="qw">
          {data.big && <div className="qw-big">{data.big}</div>}
          {data.summary && <div className="muted small" style={{ marginBottom: 8 }}>{data.summary}</div>}
          {data.series ? <FlowBars series={data.series} /> : null}
          {!data.series && (data.rows.length ? data.rows.slice(0, 30).map((r, i) => {
            const main = data.columns.length >= 3 ? 1 : 0;
            const rest = r.filter((c, j) => j !== main && c !== null && c !== "");
            return (
              <div key={i} className="lrow" onClick={() => data.links[i] && navigate(data.links[i]!)}>
                <div className="grow"><div className="tt">{String(r[main] ?? "")}</div><div className="mm">{rest.join(" · ")}</div></div>
              </div>
            );
          }) : <Empty icon="tick">{data.empty}</Empty>)}
        </div>
      )}
    </Card>
  );
}

function FlowBars({ series }: { series: NonNullable<QueryResult["series"]> }): JSX.Element {
  const max = Math.max(1, ...series.map((s) => Math.max(s.inCents, s.outCents)));
  const W = 560, H = 120, bw = W / series.length;
  return (
    <svg viewBox={`0 0 ${W} ${H + 22}`} width="100%" height={H + 22} style={{ display: "block" }}>
      <line x1="0" y1={H / 2} x2={W} y2={H / 2} stroke="var(--border)" />
      {series.map((s, i) => {
        const hi = (s.inCents / max) * (H / 2 - 4), ho = (s.outCents / max) * (H / 2 - 4);
        return (
          <g key={s.day}>
            <title>{`${s.day.split("-").reverse().join("/")}: entra ${(s.inCents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} · sai ${(s.outCents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`}</title>
            {hi > 0 && <rect x={i * bw + 1} y={H / 2 - hi} width={bw - 2} height={hi} fill="var(--green, #2F7D4F)" rx="2" />}
            {ho > 0 && <rect x={i * bw + 1} y={H / 2} width={bw - 2} height={ho} fill="var(--red)" rx="2" />}
            {i % 5 === 0 && <text x={i * bw + bw / 2} y={H + 16} textAnchor="middle" fontSize="10" fill="var(--muted)">{s.day.slice(8)}/{s.day.slice(5, 7)}</text>}
          </g>
        );
      })}
    </svg>
  );
}

// ------------------------------------------------------------ post-its

export interface NoteLite { id: string; text: string; color: string; remindAt: string | null; pinned: boolean; boardId: string }
export function PostitsWidget(): JSX.Element {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["mural-meus"], queryFn: () => api.get<{ boardId: string; notes: NoteLite[] }>("/mural/meus"), refetchInterval: 120_000 });
  const [text, setText] = useState("");
  const add = async () => {
    if (!text.trim()) return;
    try { await api.post("/mural/notas", { text: text.trim() }); setText(""); await qc.invalidateQueries({ queryKey: ["mural-meus"] }); }
    catch (e) { toast.error((e as Error).message); }
  };
  const done = async (id: string) => { await api.patch(`/mural/notas/${id}`, { done: true }); await qc.invalidateQueries({ queryKey: ["mural-meus"] }); };
  return (
    <Card title="Meus post-its" actions={<button className="link-btn" onClick={() => navigate("/mural")}>Mural →</button>}>
      <div className="row nowrap" style={{ marginBottom: 10 }}>
        <input className="input" placeholder="Anotar algo… (Enter)" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void add(); }} />
      </div>
      {!data ? <Loading /> : data.notes.length ? (
        <div className="mini-notes">
          {data.notes.map((n) => (
            <div key={n.id} className={`mini-note c-${n.color}`} onClick={() => navigate(`/mural?nota=${n.id}`)}>
              <div className="t hand">{n.text}</div>
              <div className="row nowrap small" style={{ marginTop: 4 }}>
                {n.remindAt && <span className="muted">⏰ {new Date(n.remindAt).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>}
                <span className="spacer" />
                <button className="link-btn small" title="Concluir" onClick={(e) => { e.stopPropagation(); void done(n.id); }}>✓</button>
              </div>
            </div>
          ))}
        </div>
      ) : <Empty icon="tick">Nenhum post-it aberto.</Empty>}
    </Card>
  );
}

// ------------------------------------------------------------ chat

interface ConvLite { id: string; kind: string; title: string; unread: number; last: { body: string; who: string } | null }
export function ChatWidget(): JSX.Element {
  const { data } = useQuery({ queryKey: ["chat-conversas"], queryFn: () => api.get<ConvLite[]>("/chat/conversas"), refetchInterval: 60_000 });
  const list = (data ?? []).filter((c) => c.kind !== "bot" && (c.unread || c.last)).sort((a, b) => b.unread - a.unread).slice(0, 6);
  return (
    <Card title="Chat — mensagens" actions={<button className="link-btn" onClick={() => navigate("/chat")}>Abrir chat →</button>}>
      {!data ? <Loading /> : list.length ? list.map((c) => (
        <div key={c.id} className="lrow" onClick={() => navigate(`/chat?c=${c.id}`)}>
          <Icon name={c.kind === "dm" ? "user" : c.kind === "processo" ? "scale" : "chat"} />
          <div className="grow"><div className="tt">{c.title}</div>{c.last && <div className="mm">{c.last.who}: {c.last.body}</div>}</div>
          {c.unread > 0 && <span className="nav-badge">{c.unread}</span>}
        </div>
      )) : <Empty icon="chat">Nenhuma mensagem.</Empty>}
    </Card>
  );
}
